import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Star } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";

const empty = { name: "", role: "", comment: "", rating: 5, photo_path: null, approved: true, order: 0 };

export default function TestimonialsAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/testimonials")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: editing.name.trim(),
        role: editing.role || null,
        comment: editing.comment.trim(),
        rating: Math.max(1, Math.min(5, Number(editing.rating) || 5)),
        photo_path: editing.photo_path || null,
        approved: !!editing.approved,
        order: Number(editing.order) || 0,
      };
      if (editing.id) {
        const { data } = await api.patch(`/admin/testimonials/${editing.id}`, body);
        setRows((rs) => (Array.isArray(rs) ? rs : []).map((r) => r.id === data.id ? data : r));
      } else {
        const { data } = await api.post("/admin/testimonials", body);
        setRows((rs) => [...(Array.isArray(rs) ? rs : []), data]);
      }
      toast.success("Guardado");
      setEditing(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar testimonio?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    setRows((rs) => (Array.isArray(rs) ? rs : []).filter((r) => r.id !== id));
    toast.success("Eliminado");
  };

  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <div data-testid="testimonials-admin">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-2">Testimonios</div>
          <h1 className="font-display text-3xl md:text-4xl">Reseñas de clientes</h1>
        </div>
        <button data-testid="t-new" onClick={() => setEditing({ ...empty })} className="btn-gold px-4 py-2.5 rounded-full text-sm inline-flex items-center gap-2"><Plus size={14} /> Nuevo</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {safeRows.map((t) => (
          <div key={t.id} data-testid={`t-item-${t.id}`} className="card-lux p-5 flex flex-col">
            <div className="flex items-center gap-1 mb-3">
              {[0,1,2,3,4].map((k) => <Star key={k} size={13} className={k < t.rating ? "star-on fill-current" : "star-off"} />)}
              <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${t.approved ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-[#3A3229] text-[#F5EFE0]/50"}`}>
                {t.approved ? "Aprobado" : "Oculto"}
              </span>
            </div>
            <p className="text-sm text-[#F5EFE0]/80 italic font-display line-clamp-4">"{t.comment}"</p>
            <div className="mt-4 pt-3 border-t border-[#3A3229] text-sm flex items-center justify-between">
              <div>
                <div className="text-[#F5EFE0]">{t.name}</div>
                <div className="text-xs text-[#F5EFE0]/50">{t.role || ""}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...t })} data-testid={`t-edit-${t.id}`} className="p-1.5 rounded-md border border-[#3A3229] text-[#E8C87A] hover:border-[#D4AF37]"><Pencil size={13} /></button>
                <button onClick={() => del(t.id)} data-testid={`t-del-${t.id}`} className="p-1.5 rounded-md border border-[#3A3229] text-[#F5EFE0]/60 hover:border-rose-500/60 hover:text-rose-400"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {safeRows.length === 0 && <div className="card-lux p-10 col-span-full text-center text-[#F5EFE0]/50">Añade tu primer testimonio.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 px-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card-lux w-full max-w-lg p-6 md:p-8 bg-[#171412] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl">{editing.id ? "Editar testimonio" : "Nuevo testimonio"}</h2>
              <button onClick={() => setEditing(null)} className="p-1 text-[#F5EFE0]/60"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <ImageUpload path={editing.photo_path} onChange={(p) => setEditing((f) => ({ ...f, photo_path: p }))} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Nombre</label>
                  <input data-testid="t-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Rol / Empresa</label>
                  <input value={editing.role || ""} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#F5EFE0]/60 block mb-1">Comentario</label>
                <textarea data-testid="t-comment" value={editing.comment} onChange={(e) => setEditing({ ...editing, comment: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 min-h-[100px] resize-none outline-none focus:border-[#D4AF37]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Estrellas</label>
                  <select value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]">
                    {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} estrella{n>1?"s":""}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 block mb-1">Orden</label>
                  <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="w-full bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.approved} onChange={(e) => setEditing({ ...editing, approved: e.target.checked })} className="accent-[#D4AF37]" />
                Aprobado (visible en el sitio)
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="btn-ghost-gold px-4 py-2 rounded-full text-sm">Cancelar</button>
              <button data-testid="t-save" onClick={save} disabled={saving} className="btn-gold px-4 py-2 rounded-full text-sm">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
